import { useState, useMemo, useEffect } from 'react';
import { MapTile } from './components/MapTile';
import { ConsumptionOverview } from './components/ConsumptionOverview';
// import { Updates } from './components/Updates';
import { UsageComparison } from './components/UsageComparison';
import { FilterSitesProvince } from './components/FilterSitesProvince';
import { WeatherCard } from './components/WeatherCard';
import { mockProperties, EFFICIENCY_THRESHOLD, EFFICIENCY_COLOR } from './mock-data';
import { mapSitetoSiteGeo } from './utils';
import { useDemoMode } from './hooks/useDemoMode';
import type { TimePeriod } from './types';
import { useMultiPropertySites } from '@/api/hooks/useMultiPropertySites';
import { transformApiSitesToProperties, extractRegionsFromSites } from './services/dataTransformers';
import type { MultiPropertySitesResponse } from '@/api/multi-property/types';
import { useMultiSiteMapData } from '@/api/hooks/useMapAggregatedData';
import { format, startOfMonth, endOfMonth, startOfYear, endOfYear, subYears } from 'date-fns';

export default function MapPage() {
  const [selectedRegion, setSelectedRegion] = useState<string>('All');
  const [selectedPeriod, setSelectedPeriod] = useState<TimePeriod>('this-month');
  
  const isDemoMode = useDemoMode();
  
  // Calculate date ranges based on selected period
  const dateRange = useMemo(() => {
    const now = new Date();
    let startDate: Date;
    let endDate: Date;

    switch (selectedPeriod) {
      case 'this-month':
        startDate = startOfMonth(now);
        endDate = endOfMonth(now);
        break;
      case 'this-year':
        startDate = startOfYear(now);
        endDate = endOfYear(now);
        break;
      case 'all-time':
        // Use 3 years of data for all-time
        startDate = subYears(startOfYear(now), 3);
        endDate = endOfYear(now);
        break;
      default:
        startDate = startOfMonth(now);
        endDate = endOfMonth(now);
    }

    return {
      start: format(startDate, "yyyy-MM-dd'T'00:00:00.000+07:00"),
      end: format(endDate, "yyyy-MM-dd'T'23:59:59.999+07:00")
    };
  }, [selectedPeriod]);
  
  // Always call the API hook, but disable it based on demo mode
  const { 
    data: apiSitesResponse, 
    isLoading: isLoadingApiSites, 
    error: apiSitesError 
  } = useMultiPropertySites({ enabled: !isDemoMode });

  // Type assertion to help TypeScript understand the response structure
  const typedApiResponse = apiSitesResponse as MultiPropertySitesResponse | undefined;

  // Transform and prepare site data
  const multiPropertyData = useMemo(() => {
    if (isDemoMode) {
      return mockProperties;
    } else {
      if (typedApiResponse?.data) {
        return transformApiSitesToProperties(typedApiResponse.data);
      }
      // Return empty array while loading or on error (error handling will be added below)
      return [];
    }
  }, [isDemoMode, typedApiResponse?.data]);


  // Extract available regions from API data
  const availableRegions = useMemo(() => {
    if (isDemoMode) {
      // Extract regions from mock data
      const regions = new Set(mockProperties.map(site => site.region));
      return ['All', ...Array.from(regions).sort()];
    } else {
      if (typedApiResponse?.data) {
        return extractRegionsFromSites(typedApiResponse.data);
      }
      return ['All']; // Default fallback
    }
  }, [isDemoMode, typedApiResponse?.data]);

  // Initialize selectedSites with all site codes once data is loaded
  const [selectedSites, setSelectedSites] = useState<string[]>([]);
  
  // Update selectedSites when multiPropertyData changes
  useEffect(() => {
    if (multiPropertyData.length > 0 && selectedSites.length === 0) {
      setSelectedSites(multiPropertyData.map(p => p.code));
    }
  }, [multiPropertyData, selectedSites.length]);

  // Prepare sites with area for data fetching - only online sites
  const sitesWithArea = useMemo(() => {
    if (isDemoMode || !typedApiResponse?.data) {
      return [];
    }
    // Filter to only include online sites
    const onlineSiteIds = multiPropertyData
      .filter(site => site.isOnline)
      .map(site => site.id);
    
    return typedApiResponse.data
      .filter(site => onlineSiteIds.includes(site.id))
      .map(site => ({
        siteId: site.id,
        area: site.area
      }));
  }, [isDemoMode, typedApiResponse?.data, multiPropertyData]);

  // Fetch aggregated data for online sites only
  const {
    data: aggregatedData,
    isLoading: isLoadingAggregatedData
  } = useMultiSiteMapData(
    sitesWithArea,
    dateRange.start,
    dateRange.end,
    !isDemoMode && !!typedApiResponse?.data && sitesWithArea.length > 0
  );

  // Enhance multiPropertyData with real aggregated data
  const enhancedMultiPropertyData = useMemo(() => {
    if (isDemoMode || !aggregatedData || aggregatedData.length === 0) {
      return multiPropertyData;
    }

    return multiPropertyData.map(property => {
      const siteData = aggregatedData.find(data => data?.siteId === property.id);
      
      if (!siteData) {
        // This is expected for offline sites
        return property;
      }

      // Calculate EUI based on yearly projection
      // Use actual day count from the API data
      const actualDayCount = siteData.energy.actualDayCount || 1;
      
      // Get area from API response
      const siteInfo = typedApiResponse?.data?.find(s => s.id === property.id);
      const area = siteInfo?.area || 0;
      
      // Calculate yearly total energy consumption (365 days)
      const yearlyTotalEnergy = actualDayCount > 0 ? (siteData.energy.totalEnergy / actualDayCount) * 365 : 0;
      
      // Calculate correct EUI = Yearly Total Energy Consumption / Area
      const eui = area > 0 ? yearlyTotalEnergy / area : 0;

      return {
        ...property,
        efficiency: Math.round(eui) || null,
        metrics: {
          powerConsumption: Math.round(siteData.energy.gridEnergy / actualDayCount), // Daily average
          electricityConsumption: Math.round(siteData.energy.totalEnergy / actualDayCount), // Daily average
          accumulatedRth: Math.round(siteData.occupancy.totalOccupancy)
        },
        // Add all calculated metrics
        energy: {
          solarEnergy: siteData.energy.solarEnergy,
          gridEnergy: siteData.energy.gridEnergy,
          totalEnergy: siteData.energy.totalEnergy,
          yearlyTotalEnergy,
          actualDayCount,
          eui,
          energySavingSaved: siteData.energy.energySavingSaved,
          totalEnergySavingSaved: siteData.energy.totalEnergySavingSaved,
          carbonEmissionSaved: siteData.energy.carbonEmissionSaved,
          equivalentTreesPlanted: siteData.energy.equivalentTreesPlanted
        },
        occupancy: {
          inCount: siteData.occupancy.inCount,
          outCount: siteData.occupancy.outCount,
          totalOccupancy: siteData.occupancy.totalOccupancy
        },
        temperature: {
          avgIndoor: siteData.temperature.avgIndoor,
          avgOutdoor: siteData.temperature.avgOutdoor
        }
      };
    });
  }, [multiPropertyData, aggregatedData, isDemoMode, typedApiResponse?.data]);


  // *** ALL HOOKS MUST BE CALLED BEFORE ANY CONDITIONAL RETURNS ***
  const sitesGeoData = useMemo(() => {
    return enhancedMultiPropertyData.map(site => mapSitetoSiteGeo(site));
  }, [enhancedMultiPropertyData]);

  const filterSitesGeo = useMemo(() => {
    return selectedSites.length <= 3
    ? sitesGeoData
      .filter((site) => selectedSites?.includes(site.code))
      ?.map((site) => ({
        ...site,
        lat: site.realLat ?? site.lat,
        lon: site.realLon ?? site.lon,
      }))
    : sitesGeoData.filter((site) => selectedSites?.includes(site.code));
  }, [selectedSites, sitesGeoData]);

  const onlineSites = useMemo(() => {
    return enhancedMultiPropertyData
      .filter(site => site.isOnline)
      .map(site => site.code);
  }, [enhancedMultiPropertyData]);

  const offlineSites = useMemo(() => {
    return enhancedMultiPropertyData
      .filter(site => !site.isOnline)
      .map(site => site.code);
  }, [enhancedMultiPropertyData]);

  const alarmSites = useMemo(() => {
    return enhancedMultiPropertyData
      .filter(site => site.hasAlarm)
      .map(site => site.code);
  }, [enhancedMultiPropertyData]);

  const { minLon, minLat, maxLon, maxLat } = useMemo(() => {
    let minLon = filterSitesGeo.length
      ? filterSitesGeo?.reduce((acc, site) => Math.min(acc, site.lon), 300)
      : 0;
    let minLat = filterSitesGeo.length
      ? filterSitesGeo?.reduce((acc, site) => Math.min(acc, site.lat), 300)
      : 0;
    let maxLon = filterSitesGeo.length
      ? filterSitesGeo?.reduce((acc, site) => Math.max(acc, site.lon), -300)
      : 0;
    let maxLat = filterSitesGeo.length
      ? filterSitesGeo?.reduce((acc, site) => Math.max(acc, site.lat), -300)
      : 0;

    const difLon = maxLon - minLon;
    if (difLon < 0.004) {
      minLon -= 0.002 - difLon / 2;
      maxLon += 0.002 - difLon / 2;
    }
    const difLat = maxLat - minLat;
    if (difLat < 0.004) {
      minLat -= 0.002 - difLat / 2;
      maxLat += 0.002 - difLat / 2;
    }

    return { minLon, minLat, maxLon, maxLat };
  }, [filterSitesGeo]);

  // Calculate aggregated metrics for all selected sites
  const aggregatedMetrics = useMemo(() => {
    if (!aggregatedData || aggregatedData.length === 0) {
      return null;
    }

    // Filter aggregated data for selected sites only
    const selectedSitesData = aggregatedData.filter(data => {
      if (!data) return false;
      const site = enhancedMultiPropertyData.find(s => s.id === data.siteId);
      return site && selectedSites.includes(site.code);
    });

    if (selectedSitesData.length === 0) {
      return null;
    }

    // Calculate per-site metrics
    const perSiteMetrics = selectedSitesData.map(data => {
      if (!data) return null;
      
      const site = enhancedMultiPropertyData.find(s => s.id === data.siteId);
      const siteInfo = typedApiResponse?.data?.find(s => s.id === data.siteId);
      
      if (!site || !siteInfo) return null;

      // Calculate EUI based on yearly projection
      const totalEnergy = (data.energy?.totalEnergy || 0);
      
      // Use actual day count from the data (provided by useMapAggregatedData)
      const actualDayCount = data.energy?.actualDayCount || 1;
      
      // Calculate yearly total energy consumption (365 days)
      const yearlyTotalEnergy = actualDayCount > 0 ? (totalEnergy / actualDayCount) * 365 : 0;
      
      // Calculate correct EUI = Yearly Total Energy Consumption / Area
      const eui = (siteInfo.area && siteInfo.area > 0) ? yearlyTotalEnergy / siteInfo.area : 0;
      
      const energySavingSaved = data.energy?.energySavingSaved || 5000; // Mock 5000 kWh as fallback
      const totalEnergySavingSaved = data.energy?.totalEnergySavingSaved || (energySavingSaved + (data.energy?.solarEnergy || 0));
      const carbonEmissionSaved = data.energy?.carbonEmissionSaved || (totalEnergySavingSaved * 0.4999);
      const equivalentTreesPlanted = data.energy?.equivalentTreesPlanted || (carbonEmissionSaved / 33.5);

      return {
        siteId: data.siteId,
        siteCode: site.code,
        siteName: site.name,
        energy: {
          solarEnergy: data.energy?.solarEnergy || 0,
          gridEnergy: data.energy?.gridEnergy || 0,
          totalEnergy,
          yearlyTotalEnergy,
          actualDayCount,
          eui,
          energySavingSaved,
          totalEnergySavingSaved,
          carbonEmissionSaved,
          equivalentTreesPlanted
        },
        occupancy: {
          inCount: data.occupancy?.inCount || 0,
          outCount: data.occupancy?.outCount || 0,
          totalOccupancy: data.occupancy?.totalOccupancy || 0
        },
        temperature: {
          avgIndoor: data.temperature?.avgIndoor || 0,
          avgOutdoor: data.temperature?.avgOutdoor || 0
        },
        area: siteInfo.area || 0
      };
    }).filter(Boolean);

    // Calculate totals according to the calculation logic
    const totalSolarEnergy = selectedSitesData.reduce((sum, data) => sum + (data?.energy?.solarEnergy || 0), 0);
    const totalGridEnergy = selectedSitesData.reduce((sum, data) => sum + (data?.energy?.gridEnergy || 0), 0);
    const totalEnergy = totalSolarEnergy + totalGridEnergy; // Calculation 1

    const totalInCount = selectedSitesData.reduce((sum, data) => sum + (data?.occupancy?.inCount || 0), 0);
    const totalOutCount = selectedSitesData.reduce((sum, data) => sum + (data?.occupancy?.outCount || 0), 0);
    const totalOccupancy = totalInCount - totalOutCount; // Calculation 2

    // Calculate average EUI across selected sites (Calculation 3)
    const totalArea = selectedSitesData.reduce((sum, data) => {
      const site = typedApiResponse?.data?.find(s => s.id === data?.siteId);
      return sum + (site?.area || 0);
    }, 0);
    
    // Get the average actual day count from all selected sites
    // This ensures we use the actual days with data, not the requested date range
    const totalActualDays = selectedSitesData.reduce((sum, data) => sum + (data?.energy?.actualDayCount || 1), 0);
    const avgActualDayCount = selectedSitesData.length > 0 ? totalActualDays / selectedSitesData.length : 1;
    
    // Calculate yearly total energy consumption (365 days)
    const yearlyTotalEnergy = avgActualDayCount > 0 ? (totalEnergy / avgActualDayCount) * 365 : 0;
    
    // Calculate average EUI = Yearly Total Energy Consumption / Total Area
    const averageEUI = totalArea > 0 ? yearlyTotalEnergy / totalArea : 0;

    // Energy Saving calculations (Calculations 5-8)
    const energySavingSaved = 5000 * selectedSitesData.length; // Mock 5000 kWh per site per day
    const totalEnergySavingSaved = energySavingSaved + totalSolarEnergy; // Calculation 6
    const carbonEmissionSaved = totalEnergySavingSaved * 0.4999; // Calculation 7
    const equivalentTreesPlanted = carbonEmissionSaved / 33.5; // Calculation 8

    // Temperature averages (Calculations 9-10)
    const outdoorTemps = selectedSitesData
      .map(data => data?.temperature?.avgOutdoor || 0)
      .filter(temp => temp > 0);
    const indoorTemps = selectedSitesData
      .map(data => data?.temperature?.avgIndoor || 0)
      .filter(temp => temp > 0);

    const avgOutdoorTemp = outdoorTemps.length > 0 
      ? outdoorTemps.reduce((sum, temp) => sum + temp, 0) / outdoorTemps.length 
      : 0;
    const avgIndoorTemp = indoorTemps.length > 0 
      ? indoorTemps.reduce((sum, temp) => sum + temp, 0) / indoorTemps.length 
      : 0;

    return {
      // Totals
      totalEnergy,
      totalSolarEnergy,
      totalGridEnergy,
      totalOccupancy,
      totalInCount,
      totalOutCount,
      averageEUI,
      energySavingSaved,
      totalEnergySavingSaved,
      carbonEmissionSaved,
      equivalentTreesPlanted,
      avgOutdoorTemp,
      avgIndoorTemp,
      selectedSitesCount: selectedSitesData.length,
      totalArea,
      // Per-site breakdown
      perSiteMetrics
    };
  }, [aggregatedData, selectedSites, enhancedMultiPropertyData, typedApiResponse?.data]);



  // Loading state - only wait for sites data
  if (!isDemoMode && isLoadingApiSites) {
    return (
      <div className="h-full bg-bg p-3 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
          <p className="text-dark-grey">Loading sites data...</p>
        </div>
      </div>
    );
  }

  // Error state
  if (!isDemoMode && apiSitesError) {
    console.error('API Sites Error Details:', apiSitesError);
    return (
      <div className="h-full bg-bg p-3 flex items-center justify-center">
        <div className="text-center">
          <div className="text-danger mb-4">
            <svg className="w-12 h-12 mx-auto" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
            </svg>
          </div>
          <h3 className="text-lg font-medium text-dark-grey mb-2">Failed to load sites data</h3>
          <p className="text-dark-grey mb-4">Error: {apiSitesError?.message || 'Unable to fetch data from the server.'}</p>
          <button 
            onClick={() => window.location.reload()} 
            className="px-4 py-2 bg-primary text-white rounded-md hover:bg-primary/90"
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  // Debug: If not in demo mode, not loading, no error, but no data - fall back to demo mode temporarily
  if (!isDemoMode && !isLoadingApiSites && !apiSitesError && !typedApiResponse) {
    console.warn('API call completed but no data received. This may indicate auth issues or data structure mismatch.');
    console.warn('Falling back to demo mode temporarily. Check authentication and API response structure.');
    // Temporarily use mock data to prevent empty state
  }

  return (
    <div className="h-full bg-bg p-3 flex flex-col">
      <div className="grid grid-cols-12 gap-3 flex-1 min-h-0">
        {/* Map Tile Component */}
        <div className="flex flex-col col-span-8 min-h-0 overflow-hidden">
          <div className="flex justify-between items-center mb-2">
            <FilterSitesProvince
              selectedRegion={selectedRegion}
              setSelectedRegion={setSelectedRegion}
              selectedSites={selectedSites}
              setSelectedSites={setSelectedSites}
              sitesGeoData={sitesGeoData}
              availableRegions={availableRegions}
            />
            <div className="flex items-center gap-2">
              <button
                onClick={() => {
                  if (
                    selectedSites?.filter((site: string) =>
                      onlineSites.includes(site)
                    )?.length === onlineSites.length
                  )
                    setSelectedSites(
                      selectedSites?.filter(
                        (site: string) => !onlineSites.includes(site)
                      ) ?? []
                    );
                  else
                    setSelectedSites([
                      ...onlineSites.filter(
                        (site: any) => !selectedSites?.includes(site)
                      ),
                      ...(selectedSites ?? []),
                    ]);
                }}
                className={`px-3.5 py-1.5 rounded-md text-xs font-medium transition-all flex items-center gap-1.5 ${selectedSites?.filter((site: string) =>
                  onlineSites.includes(site)
                )?.length === onlineSites.length
                  ? 'bg-green/10 text-green border-green/20 hover:bg-green/20'
                  : 'bg-white text-dark-grey border-light-grey hover:border-green/40 hover:bg-green/5'
                  } border shadow-sm`}
              >
                {`Online ${onlineSites.length}`}
              </button>
              <button
                onClick={() => {
                  if (
                    selectedSites?.filter((site: string) =>
                      offlineSites.includes(site)
                    )?.length === offlineSites.length
                  )
                    setSelectedSites(
                      selectedSites?.filter(
                        (site: string) => !offlineSites.includes(site)
                      ) ?? []
                    );
                  else
                    setSelectedSites([
                      ...offlineSites.filter(
                        (site: any) => !selectedSites?.includes(site)
                      ),
                      ...(selectedSites ?? []),
                    ]);
                }}
                className={`px-3.5 py-1.5 rounded-md text-xs font-medium transition-all flex items-center gap-1.5 ${selectedSites?.filter((site: string) =>
                  offlineSites.includes(site)
                )?.length === offlineSites.length
                  ? 'bg-gray-100 text-gray-700 border-gray-300 hover:bg-gray-200'
                  : 'bg-white text-dark-grey border-light-grey hover:border-gray-400 hover:bg-gray-50'
                  } border shadow-sm`}
              >
                {`Offline ${offlineSites.length}`}
              </button>
              <button
                onClick={() => {
                  if (
                    selectedSites?.filter((site: string) =>
                      alarmSites.includes(site)
                    )?.length === alarmSites.length
                  )
                    setSelectedSites(
                      selectedSites?.filter(
                        (site: string) => !alarmSites.includes(site)
                      ) ?? []
                    );
                  else
                    setSelectedSites([
                      ...alarmSites.filter(
                        (site: any) => !selectedSites?.includes(site)
                      ),
                      ...(selectedSites ?? []),
                    ]);
                }}
                className={`px-3.5 py-1.5 rounded-md text-xs font-medium transition-all flex items-center gap-1.5 ${selectedSites?.filter((site: string) =>
                  alarmSites.includes(site)
                )?.length === alarmSites.length
                  ? 'bg-danger/10 text-danger border-danger/20 hover:bg-danger/20'
                  : 'bg-white text-dark-grey border-light-grey hover:border-danger/40 hover:bg-danger/5'
                  } border shadow-sm`}
              >
                {`Alarms ${alarmSites.length}`}
              </button>
            </div>
          </div>
          <div className="relative flex-1 bg-white rounded-lg shadow-sm border border-light-grey overflow-hidden min-h-0">
            <MapTile
              data={filterSitesGeo}
              onClickSiteButton={(siteId: any) => {
                console.log('Navigate to site:', siteId);
              }}
              onClickSite={(siteId: any) => {
                const currentSite = enhancedMultiPropertyData.find(
                  (site: any) => site.id === siteId
                )?.code;
                if (currentSite) {
                  setSelectedSites([currentSite]);
                }
              }}
              onResetView={() => {
                setSelectedSites(enhancedMultiPropertyData.map(p => p.code));
              }}
              minLon={minLon}
              minLat={minLat}
              maxLon={maxLon}
              maxLat={maxLat}
            />
            {/* EUI Scale - Top Left */}
            <div className="absolute top-3 left-3">
              <div className="bg-white rounded-lg shadow-sm border border-light-grey p-3">
                <div className="flex items-center gap-1.5 mb-2">
                  <div className="w-[18px] h-[18px] bg-light-grey rounded flex items-center justify-center">
                    <svg className="w-3 h-3 text-primary" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 10V3L4 14h7v7l9-11h-7z" />
                    </svg>
                  </div>
                  <span className="heading-7 text-dark-blue">EUI</span>
                </div>
                <div className="mb-2">
                  <p className="text-[9px] text-gray-500">BEC Standard</p>
                  <p className="text-[9px] text-gray-500"><span className="font-semibold">Retail & Wholesale</span></p>
                </div>
                <div className="space-y-1.5">
                  {EFFICIENCY_THRESHOLD.map((threshold, index) => {
                    const labels = ['ZEB', 'ECON', 'HEPS', 'BEC', 'REF'];
                    return (
                      <div key={index} className="flex items-center gap-2">
                        <div
                          style={{ backgroundColor: EFFICIENCY_COLOR[index] }}
                          className="w-3 h-3 rounded-sm"
                        />
                        <div className="flex-1 flex items-center justify-between">
                          <span className="paragraph-9 font-medium text-dark-grey">{labels[index]}</span>
                          <span className="paragraph-9 text-gray-500">{'<'}{threshold}</span>
                        </div>
                      </div>
                    );
                  })}
                </div>
                <div className="mt-2 pt-2 border-t border-light-grey">
                  <p className="text-[8px] text-gray-500">kWh/m² per year</p>
                </div>
              </div>
            </div>
            
            {/* Weather Card - Bottom Left */}
            <div className="absolute bottom-0">
              <WeatherCard province={selectedRegion} isDemoMode={isDemoMode} />
            </div>
          </div>
        </div>
        <div className="col-span-4 flex flex-col min-h-0">
          <div className="space-y-3 overflow-y-auto flex-1">
          <ConsumptionOverview
            data={enhancedMultiPropertyData}
            selectedSites={selectedSites}
            isDemoMode={isDemoMode}
            selectedPeriod={selectedPeriod}
            onPeriodChange={setSelectedPeriod}
            aggregatedData={aggregatedData}
            isLoadingAggregatedData={isLoadingAggregatedData}
            aggregatedMetrics={aggregatedMetrics}
            dateRange={dateRange}
          />
          <UsageComparison
            selectedSites={selectedSites}
            sites={enhancedMultiPropertyData.filter(site =>
              selectedSites.includes(site.code)
            )}
            isDemoMode={isDemoMode}
            selectedPeriod={selectedPeriod}
            aggregatedData={aggregatedData}
            isLoadingAggregatedData={isLoadingAggregatedData}
            aggregatedMetrics={aggregatedMetrics}
          />
          </div>
        </div>
      </div>
    </div>
  );
}